import Component from '../Component';
import generateElementId from '../utils/generateElementId';
import Switch from './Switch';
import Select from './Select';
import UploadImageButton from './UploadImageButton';
import classList from '../utils/classList';
import ColorPreviewInput from './ColorPreviewInput';
import IconInput from './IconInput';
import ItemList from '../utils/ItemList';
import MultiSelect from './MultiSelect';
import FieldSet from './FieldSet';

/**
 * A type that matches any valid value for the `type` attribute on an HTML `<input>` element.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-type
 *
 * Note: this will be exported from a different location in the future.
 *
 * @see https://github.com/flarum/core/issues/3039
 */

/**
 * Valid options for the setting component builder to generate an HTML input element.
 */

const BooleanSettingTypes = ['bool', 'checkbox', 'switch', 'boolean'];
const SelectSettingTypes = ['select', 'dropdown', 'selectdropdown'];
const RadioSettingTypes = ['radio'];
const TextareaSettingTypes = ['textarea'];
const ColorPreviewSettingType = 'color-preview';
const IconSettingType = 'icon';
const ImageUploadSettingType = 'image-upload';
const StackedFormControlType = 'stacked-text';

/**
 * Valid options for the setting component builder to generate a Switch.
 */

/**
 * Valid options for the setting component builder to generate a Select dropdown.
 */

/**
 * Valid options for the setting component builder to generate a Textarea.
 */

/**
 * Valid options for the setting component builder to generate a ColorPreviewInput.
 */

/**
 * Valid options for the setting component builder to generate an IconInput:
 * a Font Awesome class field with an icon picker.
 */

/**
 * All valid options for the setting component builder.
 */

/**
 * Builds a field component based on the provided attributes.
 * Depending on the type of input, you can set the type to 'bool', 'select', or
 * any standard <input> type. Any values inside the 'extra' object will be added
 * to the component as an attribute.
 *
 * Alternatively, you can pass a callback that will be executed in ExtensionPage's
 * context to include custom JSX elements.
 *
 * @example
 *
 * <FormGroup key="acme.checkbox"
 *            label={app.translator.trans('acme.admin.setting_label')}
 *            type="bool"
 *            help={app.translator.trans('acme.admin.setting_help')}
 *            className="Setting-item" />
 *
 * @example
 *
 * <FormGroup key="acme.select"
 *            label={app.translator.trans('acme.admin.setting_label')}
 *            type="select"
 *            options={{
 *              'option1': 'Option 1 label',
 *              'option2': 'Option 2 label',
 *            }}
 *            default="option1" />
 */
export default class FormGroup extends Component {
  inputId = (() => generateElementId())();
  helpTextId = (() => generateElementId())();
  view(vnode) {
    const customFieldComponents = this.customFieldComponents();
    const {
      key,
      help,
      type,
      label,
      stream,
      getSetting,
      containerClassName,
      ...componentAttrs
    } = this.attrs;

    // TypeScript being TypeScript
    const attrs = componentAttrs;
    const value = stream ? stream() : null;
    const {
      inputId,
      helpTextId
    } = this;
    let settingElement;

    // Typescript being Typescript
    // https://github.com/microsoft/TypeScript/issues/14520
    if (BooleanSettingTypes.includes(type)) {
      const switchHelpTextId = help ? helpTextId : undefined;
      return m("div", {
        className: classList('Form-group', containerClassName)
      }, m(Switch, Object.assign({
        state: !!value && value !== '0',
        onchange: stream,
        inputAttrs: switchHelpTextId ? {
          'aria-describedby': switchHelpTextId
        } : undefined
      }, attrs), label), help ? m("div", {
        id: switchHelpTextId,
        className: "helpText"
      }, help) : null);
    } else if (SelectSettingTypes.includes(type)) {
      const {
        default: defaultValue,
        options,
        multiple,
        ...otherAttrs
      } = attrs;
      const Tag = multiple ? MultiSelect : Select;
      settingElement = m(Tag, Object.assign({
        id: inputId,
        "aria-describedby": helpTextId,
        value: value ?? defaultValue,
        options: options,
        onchange: stream
      }, otherAttrs));
    } else if (RadioSettingTypes.includes(type)) {
      const {
        default: defaultValue,
        options,
        multiple,
        name,
        ...otherAttrs
      } = attrs;
      settingElement = m(FieldSet, otherAttrs, options.map(_ref => {
        let {
          value,
          label
        } = _ref;
        return m("label", {
          className: "checkbox"
        }, m("input", {
          type: "radio",
          name: name || inputId,
          value: value,
          bidi: stream
        }), label);
      }));
    } else if (type === StackedFormControlType) {
      const {
        textArea,
        ...otherAttrs
      } = attrs;
      const {
        setting: textAreaSetting,
        ...textAreaAttrs
      } = textArea;
      settingElement = m("div", Object.assign({
        className: "StackedFormControl"
      }, otherAttrs), m("input", {
        type: "text",
        className: "FormControl",
        bidi: stream
      }), m("textarea", Object.assign({
        className: "FormControl",
        bidi: getSetting?.(textAreaSetting)
      }, textAreaAttrs)));
    } else if (type === ImageUploadSettingType) {
      const {
        value,
        ...otherAttrs
      } = attrs;
      settingElement = m(UploadImageButton, Object.assign({
        value: stream
      }, otherAttrs));
    } else if (customFieldComponents.has(type)) {
      return customFieldComponents.get(type)({
        ...this.attrs,
        bidi: this.attrs.stream
      });
    } else {
      attrs.className = classList('FormControl', attrs.className);
      if (TextareaSettingTypes.includes(type)) {
        settingElement = m("textarea", Object.assign({
          id: inputId,
          "aria-describedby": helpTextId,
          bidi: stream
        }, attrs));
      } else if (type === IconSettingType) {
        settingElement = m(IconInput, Object.assign({
          id: inputId,
          "aria-describedby": helpTextId,
          bidi: stream
        }, attrs));
      } else {
        let Tag = 'input';
        if (type === ColorPreviewSettingType) {
          Tag = ColorPreviewInput;
        } else {
          attrs.type = type;
        }
        settingElement = m(Tag, Object.assign({
          id: inputId,
          "aria-describedby": helpTextId,
          bidi: stream
        }, attrs));
      }
    }
    return m("div", {
      className: classList('Form-group', containerClassName)
    }, label && m("label", {
      for: inputId
    }, label), help && m("div", {
      id: helpTextId,
      className: "helpText"
    }, help), settingElement);
  }

  /**
   * A list of extension-defined custom setting components to be available.
   *
   * The ItemList key represents the value for the `type` attribute.
   * All attributes passed are provided as arguments to the function added to the ItemList.
   *
   * ItemList priority has no effect here.
   *
   * @example
   * ```tsx
   * extend(AdminPage.prototype, 'customFieldComponents', function (items) {
   *   // You can access the AdminPage instance with `this` to access its `settings` property.
   *
   *   // Prefixing the key with your extension ID is recommended to avoid collisions.
   *   items.add('my-ext.setting-component', (attrs) => {
   *     return (
   *       <div className={attrs.className}>
   *         <label>{attrs.label}</label>
   *         {attrs.help && <p className="helpText">{attrs.help}</p>}
   *
   *         My setting component!
   *       </div>
   *     );
   *   })
   * })
   * ```
   */
  customFieldComponents() {
    const items = new ItemList();
    return items;
  }
}
flarum.reg.add('core', 'common/components/FormGroup', FormGroup);